import React, { useId, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
    ScatterChart, Scatter, CartesianGrid,
    XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Label
} from 'recharts';
import {
    Box,
    Typography,
    Paper,
    useTheme,
    alpha
} from '@mui/material';

const CHART_PALETTE = [
    '#2563EB', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#F97316', '#84CC16', '#E11D48',
    '#14B8A6', '#7C3AED', '#0EA5E9', '#22C55E', '#F43F5E',
    '#6366F1', '#A855F7', '#D946EF', '#3B82F6', '#16A34A',
    '#F97316', '#CA8A04', '#DC2626', '#0F766E', '#334155'
];

const PIE_COLORS = [
    '#2563EB', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
    '#EC4899', '#06B6D4', '#F97316', '#84CC16', '#E11D48',
    '#14B8A6', '#7C3AED', '#0EA5E9', '#22C55E', '#F43F5E',
    '#6366F1', '#A855F7', '#D946EF', '#3B82F6', '#16A34A',
    '#FB7185', '#A3E635', '#FACC15', '#BE123C', '#1D4ED8'
];

const formatNumber = (num) => {
    if (num >= 1000000000) return (num / 1000000000).toFixed(2) + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    if (Number.isInteger(num)) return num.toLocaleString();
    return num.toFixed(2);
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
        return (
            <Box
                sx={{
                    p: 2,
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    background: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    minWidth: 160,
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700, color: '#1E293B', fontSize: 13 }}>
                    {label}
                </Typography>
                {payload.map((entry) => (
                    <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: entry.color, mr: 1, flexShrink: 0
                        }} />
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: 12, mr: 1 }}>
                            {entry.name}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: 12 }}>
                            {formatNumber(entry.value)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    }
    return null;
};

const RoundedBar = (props) => {
    const { x, y, width, height, fill } = props;
    const safeHeight = Math.abs(height);
    const safeY = height < 0 ? y + height : y;
    const radius = Math.min(6, width / 2);

    if (safeHeight < radius) {
        return <rect x={x} y={safeY} width={width} height={safeHeight} fill={fill} />;
    }

    return (
        <path
            d={`M${x},${safeY + safeHeight} 
                L${x},${safeY + radius} 
                Q${x},${safeY} ${x + radius},${safeY} 
                L${x + width - radius},${safeY} 
                Q${x + width},${safeY} ${x + width},${safeY + radius} 
                L${x + width},${safeY + safeHeight} Z`}
            fill={fill}
        />
    );
};

const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.04) return null;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="#475569" textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central" fontSize={11} fontWeight={500}>
            {name} ({(percent * 100).toFixed(1)}%)
        </text>
    );
};

const CustomTick = (props) => {
    const { x, y, payload } = props;
    let displayText = payload.value;
    if (displayText && displayText.length > 12) {
        displayText = displayText.substring(0, 10) + '...';
    }
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="end"
                fill="#64748B" fontSize={11} fontWeight={500} transform="rotate(-45)">
                {displayText}
            </text>
        </g>
    );
};

const CustomYTick = (props) => {
    const { x, y, payload } = props;
    const fmt = (num) => {
        if (num >= 1000000000) return (num / 1000000000).toFixed(0) + 'B';
        if (num >= 1000000) return (num / 1000000).toFixed(0) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
        return num.toString();
    };
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dx={-8} textAnchor="end"
                fill="#64748B" fontSize={11} fontWeight={500}>
                {fmt(payload.value)}
            </text>
        </g>
    );
};

const LegendFormatter = (value) => (
    <span style={{ color: '#475569', fontWeight: 500, fontSize: 12 }}>{value}</span>
);

const CHART_CONFIG_SHAPE = PropTypes.shape({
    data: PropTypes.array,
    title: PropTypes.string,
    description: PropTypes.string,
    source: PropTypes.string,
    type: PropTypes.string,
    xKey: PropTypes.string,
    yKey: PropTypes.string
});

const getVisualizations = (visualizationPayload) => {
    const rawVisualization = visualizationPayload?.visualization;
    if (!rawVisualization) return [];

    if (Array.isArray(rawVisualization)) {
        return rawVisualization.filter((item) => item && typeof item === 'object');
    }

    return typeof rawVisualization === 'object' ? [rawVisualization] : [];
};

const VisualizationChart = ({ visualizationPayload }) => {
    const theme = useTheme();
    const chartGroupId = useId();

    const visualizations = useMemo(
        () => getVisualizations(visualizationPayload),
        [visualizationPayload]
    );

    if (visualizations.length === 0) {
        return null;
    }

    const gridStroke = alpha(theme.palette.text.secondary, 0.08);
    const axisStroke = alpha(theme.palette.text.secondary, 0.15);

    const renderChart = (chart, chartIndex) => {
        const { data, type, xKey, yKey } = chart;
        const chartData = Array.isArray(data) ? data : [];

        if (!chartData.length || !xKey || !yKey) {
            return null;
        }

        const chartSuffix = `${chartGroupId}-${chartIndex}`;
        const barGradientId = `barGradient-${chartSuffix}`;
        const areaGradientId = `areaGradient-${chartSuffix}`;
        const barShadowId = `barShadow-${chartSuffix}`;

        const commonXAxisProps = {
            dataKey: xKey,
            angle: -45,
            textAnchor: "end",
            height: 80,
            tick: CustomTick,
            tickLine: false,
            axisLine: { stroke: axisStroke }
        };

        const xAxisLabel = (
            <Label
                value={xKey.replace(/_/g, ' ').length > 30
                    ? xKey.replace(/_/g, ' ').substring(0, 27) + '...'
                    : xKey.replace(/_/g, ' ')}
                position="insideBottom" offset={-15}
                style={{ textAnchor: 'middle', fill: '#334155', fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}
            />
        );

        const commonYAxisProps = {
            tick: CustomYTick,
            tickLine: false,
            axisLine: { stroke: axisStroke }
        };

        const yAxisLabel = (
            <Label
                value={yKey.replace(/_/g, ' ').length > 30
                    ? yKey.replace(/_/g, ' ').substring(0, 27) + '...'
                    : yKey.replace(/_/g, ' ')}
                angle={-90} position="insideLeft" offset={-10}
                style={{ textAnchor: 'middle', fill: '#334155', fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}
            />
        );

        const gradientDefs = (
            <defs>
                <linearGradient id={barGradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={1} />
                    <stop offset="100%" stopColor="#1E3A8A" stopOpacity={0.85} />
                </linearGradient>
                <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
                </linearGradient>
                <filter id={barShadowId} x="-5%" y="-5%" width="110%" height="120%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#1E3A8A" floodOpacity="0.15" />
                </filter>
            </defs>
        );

        const chartMargin = { top: 24, right: 32, left: 32, bottom: 80 };
        const normalizedType = type ? type.toLowerCase() : 'bar';

        try {
            switch (normalizedType) {
                case 'line':
                    return (
                        <LineChart data={chartData} margin={chartMargin}>
                            {gradientDefs}
                            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                            <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                            <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                            <Tooltip content={<CustomTooltip />} />
                            <Line type="monotone" dataKey={yKey} stroke="#2563EB"
                                strokeWidth={3} dot={{ r: 5, fill: '#fff', stroke: '#2563EB', strokeWidth: 2.5 }}
                                activeDot={{ r: 7, fill: '#2563EB', stroke: '#fff', strokeWidth: 2 }}
                                name={yKey.replace(/_/g, ' ')} animationDuration={1200}
                                animationEasing="ease-in-out" />
                        </LineChart>
                    );

                case 'area':
                    return (
                        <AreaChart data={chartData} margin={chartMargin}>
                            {gradientDefs}
                            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                            <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                            <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey={yKey} stroke="#2563EB"
                                fill={`url(#${areaGradientId})`} strokeWidth={2.5}
                                dot={{ r: 4, fill: '#fff', stroke: '#2563EB', strokeWidth: 2 }}
                                activeDot={{ r: 6, fill: '#2563EB', stroke: '#fff', strokeWidth: 2 }}
                                name={yKey.replace(/_/g, ' ')} animationDuration={1200}
                                animationEasing="ease-in-out" />
                        </AreaChart>
                    );

                case 'pie':
                    return (
                        <PieChart>
                            <Pie data={chartData} dataKey={yKey} nameKey={xKey}
                                cx="50%" cy="50%" innerRadius={70} outerRadius={160}
                                paddingAngle={3} cornerRadius={6}
                                label={renderPieLabel} labelLine={{ stroke: '#94A3B8', strokeWidth: 1 }}
                                animationDuration={1200} animationEasing="ease-out">
                                {chartData.map((entry, idx) => (
                                    <Cell key={`pie-${chartSuffix}-${idx}-${String(entry[xKey])}-${String(entry[yKey])}`} fill={PIE_COLORS[idx % PIE_COLORS.length]}
                                        stroke="none" />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                                wrapperStyle={{ paddingTop: 16, fontSize: 12 }}
                                iconType="circle" iconSize={8}
                                formatter={LegendFormatter}
                            />
                        </PieChart>
                    );

                case 'scatter':
                    return (
                        <ScatterChart margin={chartMargin}>
                            {gradientDefs}
                            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                            <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                            <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#CBD5E1', strokeDasharray: '4 4' }} />
                            <Scatter data={chartData} fill="#2563EB"
                                name={yKey.replace(/_/g, ' ')} animationDuration={1200}>
                                {chartData.map((entry, idx) => (
                                    <Cell key={`scatter-${chartSuffix}-${idx}-${String(entry[xKey])}-${String(entry[yKey])}`} fill={CHART_PALETTE[idx % CHART_PALETTE.length]}
                                        r={7} />
                                ))}
                            </Scatter>
                        </ScatterChart>
                    );

                case 'stacked_bar':
                case 'bar':
                default:
                    return (
                        <BarChart data={chartData} margin={chartMargin}
                            barSize={Math.min(40, Math.max(16, 600 / chartData.length))}>
                            {gradientDefs}
                            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                            <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                            <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey={yKey} fill={`url(#${barGradientId})`} shape={<RoundedBar />}
                                name={yKey.replace(/_/g, ' ')} animationDuration={1200}
                                animationEasing="ease-in-out" filter={`url(#${barShadowId})`} />
                        </BarChart>
                    );
            }
        } catch (error) {
            console.error('Visualization chart render failed:', error, chart);
            return null;
        }
    };

    const renderedCharts = visualizations.map((chart, chartIndex) => {
        const isValidChart =
            chart &&
            Array.isArray(chart.data) &&
            chart.data.length > 0 &&
            typeof chart.xKey === 'string' &&
            typeof chart.yKey === 'string';

        if (!isValidChart) {
            return null;
        }

        const chartView = renderChart(chart, chartIndex);
        if (!chartView) {
            return null;
        }

        return (
            <Paper
                key={`viz-card-${chartGroupId}-${chartIndex}`}
                elevation={0}
                sx={{
                    mt: chartIndex === 0 ? 2 : 3,
                    p: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderRadius: 2
                }}
            >
                {chart.title && (
                    <Typography
                        variant="h6"
                        sx={{
                            mb: 1,
                            fontWeight: 600,
                            color: '#1E293B',
                            fontSize: '14px'
                        }}
                    >
                        {chart.title}
                    </Typography>
                )}

                {chart.description && (
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 2,
                            color: '#64748B',
                            fontSize: '12px',
                            fontStyle: 'italic'
                        }}
                    >
                        {chart.description}
                    </Typography>
                )}

                <Box sx={{ width: '100%', height: 400, mb: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        {chartView}
                    </ResponsiveContainer>
                </Box>

                {chart.source && (
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            color: '#94A3B8',
                            fontSize: '11px',
                            textAlign: 'right',
                            mt: 1
                        }}
                    >
                        Source: {chart.source}
                    </Typography>
                )}
            </Paper>
        );
    }).filter(Boolean);

    if (renderedCharts.length === 0) {
        return null;
    }

    return (
        <Box>
            {renderedCharts}
        </Box>
    );
};

VisualizationChart.propTypes = {
    visualizationPayload: PropTypes.oneOfType([
        PropTypes.shape({
            status: PropTypes.string,
            visualization: PropTypes.oneOfType([
                CHART_CONFIG_SHAPE,
                PropTypes.arrayOf(CHART_CONFIG_SHAPE)
            ])
        }),
        PropTypes.object
    ])
};

export default React.memo(VisualizationChart);
